import { useLayoutEffect, useRef, useState } from "react";
import Logo from "./logo";

export default function PopupHeader({
  isPro,
  trialMode,
  trialEnded,
  remainingTrialDays,
  usageHit,
  usageRemaining,
  subscriptionStatusStep,
  notSupported,
  musicProOnly,
  isSlowedEnabled,
  contentApplying,
  mount,
  billingLoading,
  upgradeError,
  onUpgradeClick,
  onManageSubscriptionClick,
  onDebugToggle,
  onSlowedToggle,
  hideManagePlan,
}) {
  const hasTrial = trialMode && remainingTrialDays >= 0;
  const showUnlockButton = !isPro;
  const usageMinutesLeft = Math.max(
    0,
    Math.floor(Number(usageRemaining) || 0),
  );
  const usageText =
    usageMinutesLeft >= 60
      ? `${Math.floor(usageMinutesLeft / 60)}h ${usageMinutesLeft % 60}m left`
      : `${usageMinutesLeft} min left`;
  // Post-trial with free minutes still on the clock: rotate the button the
  // same way the trial does, swapping days-left for minutes-left.
  const hasUsage =
    !isPro &&
    !hasTrial &&
    trialEnded === true &&
    usageHit !== true &&
    usageMinutesLeft > 0;
  const showUsageDepletedUnlock = trialEnded === true && usageHit === true;
  const rotating = hasTrial || hasUsage;
  const step = rotating ? subscriptionStatusStep : 0;
  const daysText =
    remainingTrialDays > 0
      ? `${remainingTrialDays} ${remainingTrialDays === 1 ? "day" : "days"} left`
      : "Last day";
  const statusText = hasTrial ? "Trial mode" : "Usage mode";
  const countdownText = hasTrial ? daysText : usageText;
  const labelRefs = useRef({});
  if (!rotating) {
    delete labelRefs.current["trial-mode"];
    delete labelRefs.current["trial-days"];
  }
  const [unlockLabelWidth, setUnlockLabelWidth] = useState(null);
  useLayoutEffect(() => {
    if (!showUnlockButton) return;
    // Size to the widest label, not the active one, so the button holds a
    // fixed width across Unlock PRO → Usage mode → 10 min left.
    // scrollWidth stays correct while the button column is clipped.
    const width = Object.values(labelRefs.current).reduce(
      (widest, label) => Math.max(widest, Math.ceil(label?.scrollWidth || 0)),
      0,
    );
    if (!Number.isFinite(width) || width <= 0) return;
    setUnlockLabelWidth((previous) => (previous === width ? previous : width));
  }, [showUnlockButton, rotating, statusText, countdownText]);

  return (
    <div className="header">
      <div className="header__content">
        <div className="header__left_button">
          {showUnlockButton && (
            <button
              type="button"
              className={`subscription-status-button subscription-status-button--unlock ${showUsageDepletedUnlock ? "subscription-status-button--unlock-depleted" : ""} subscription-status-button--step-${step}`}
              style={
                unlockLabelWidth != null
                  ? { "--subscription-status-label-width": `${unlockLabelWidth}px` }
                  : undefined
              }
              onClick={onUpgradeClick}
            >
              <span
                ref={(el) => {
                  labelRefs.current.default = el;
                }}
                className="subscription-status-button__label subscription-status-button__label--default"
              >
                Unlock PRO
              </span>
              {rotating && (
                <span
                  ref={(el) => {
                    labelRefs.current["trial-mode"] = el;
                  }}
                  className="subscription-status-button__label subscription-status-button__label--trial-mode"
                >
                  {statusText}
                </span>
              )}
              {rotating && (
                <span
                  ref={(el) => {
                    labelRefs.current["trial-days"] = el;
                  }}
                  className="subscription-status-button__label subscription-status-button__label--trial-days"
                >
                  {countdownText}
                </span>
              )}
            </button>
          )}
          {upgradeError && (
            <button
              type="button"
              className="header__upgrade-error"
              onClick={onUpgradeClick}
              title="Try opening checkout again"
            >
              {upgradeError}
            </button>
          )}
          {isPro && !hideManagePlan && (
            <button
              type="button"
              className={`subscription-status-button${billingLoading ? " subscription-status-button--loading" : ""}`}
              disabled={billingLoading}
              aria-busy={billingLoading}
              onClick={onManageSubscriptionClick}
            >
              {billingLoading ? (
                <span className="subscription-status-button__loading">
                  <span className="header__spinner" aria-hidden="true" />
                  Loading
                </span>
              ) : (
                <span className="subscription-status-button__label subscription-status-button__label--default">
                  Manage plan
                </span>
              )}
            </button>
          )}
        </div>

        <div
          className="header__title"
          onDoubleClick={(e) => e.metaKey && onDebugToggle()}
        >
          <Logo isPro={isPro} trialMode={trialMode} />
        </div>

        <div className="header__right_button">
          {contentApplying ? (
            <span className="header__applying" aria-live="polite">
              <span className="header__spinner" aria-hidden="true" />
              Applying
            </span>
          ) : notSupported ? (
            <span className="header__status header__status--muted">
              Unsupported
            </span>
          ) : musicProOnly ? (
            <span className="header__status header__status--muted">
              Pro only
            </span>
          ) : !isSlowedEnabled && mount ? (
            <span className="header__status">Off</span>
          ) : null}
          <input
            type="checkbox"
            className="switcher"
            checked={isSlowedEnabled}
            disabled={
              notSupported ||
              musicProOnly ||
              contentApplying ||
              (usageHit && !isPro && !hasTrial)
            }
            onChange={() => onSlowedToggle(!isSlowedEnabled)}
          />
        </div>
      </div>

      <div className="header__bar" />
    </div>
  );
}
